import { z } from "zod"

import {
  branchIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const timestamp = z.string().datetime({ offset: false })

export const invoiceStateV1Schema = z.enum([
  "draft",
  "review_required",
  "tax_review_required",
  "finalized",
  "open",
  "partially_paid",
  "paid",
  "voided",
])

export const invoiceLineDraftV1Schema = z.object({
  lineId: entityIdSchema,
  billingTriggerId: entityIdSchema,
  serviceOrderId: entityIdSchema,
  serviceId: entityIdSchema,
  branchId: branchIdSchema,
  costCenter: z.string().trim().min(1).max(140),
  serviceLocationId: entityIdSchema,
  description: z.string().trim().min(1).max(500),
  amountMinor: z.number().int().nonnegative(),
  quotedTaxMinor: z.number().int().nonnegative(),
  taxJurisdictionCode: z.string().regex(/^[A-Z0-9][A-Z0-9._-]{1,63}$/),
  serviceCategory: z.string().regex(/^[A-Z0-9][A-Z0-9._-]{0,63}$/),
  exemptionStatus: z.enum(["taxable", "exempt", "unknown"]),
  completionAt: timestamp,
}).strict()

export const invoiceDraftCreateV1Schema = z.object({
  invoiceId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  customerId: entityIdSchema,
  billingProfileId: entityIdSchema,
  currency: z.string().regex(/^[A-Z]{3}$/),
  billingPartyKey: z.string().trim().min(1).max(140),
  purchaseOrderKey: z.string().trim().min(1).max(140).nullable(),
  termsDays: z.number().int().min(0).max(365),
  lines: z.array(invoiceLineDraftV1Schema).min(1).max(500),
}).strict()

export const invoiceFinalizeV1Schema = z.object({
  invoiceId: entityIdSchema,
  invoiceDate: timestamp,
  paymentTaxableEventAt: timestamp.nullable(),
}).strict()

export const invoiceTaxReversalLinkV1Schema = z.object({
  originalInvoiceId: entityIdSchema,
  originalTaxSnapshotIds: z.array(entityIdSchema).min(1).max(500)
    .refine((items) => new Set(items).size === items.length),
  reason: z.string().trim().min(10).max(500),
}).strict()

export const invoiceFinalizeV2Schema = invoiceFinalizeV1Schema.extend({
  taxReversal: invoiceTaxReversalLinkV1Schema.nullable(),
}).strict()

export type InvoiceDraftCreateV1 = z.infer<typeof invoiceDraftCreateV1Schema>
export type InvoiceFinalizeV1 = z.infer<typeof invoiceFinalizeV1Schema>
export type InvoiceFinalizeV2 = z.infer<typeof invoiceFinalizeV2Schema>
export type InvoiceTaxReversalLinkV1 = z.infer<typeof invoiceTaxReversalLinkV1Schema>
